import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDistanceToNow } from "date-fns";

interface DuplicateRecipient {
  email: string;
  player_name: string;
  player_id?: number;
  last_sent_date: string;
  last_subject: string;
  last_template_id?: number;
  last_template_name?: string | null;
  total_emails_sent: number;
  days_since_last_email?: number;
}

interface DuplicateEmailWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateRecipients: DuplicateRecipient[];
  onProceedWithSelected: (selectedEmails: string[]) => void;
  onCancel: () => void;
}

export default function DuplicateEmailWarningModal({
  isOpen,
  onClose,
  duplicateRecipients,
  onProceedWithSelected,
  onCancel,
}: DuplicateEmailWarningModalProps) {
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

  // Initialize with all emails selected by default
  useEffect(() => {
    if (duplicateRecipients.length > 0) {
      setSelectedEmails(new Set(duplicateRecipients.map(r => r.email)));
    }
  }, [duplicateRecipients]);

  const handleEmailToggle = (email: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedEmails(newSelected);
  };

  const handleProceed = () => {
    if (selectedEmails.size > 0) {
      onProceedWithSelected(Array.from(selectedEmails));
    }
  };

  const handleRemoveSelected = () => {
    // Get all original emails, then filter out the ones that are duplicates and NOT selected
    const allOriginalEmails = [...duplicateRecipients.map(d => d.email)];
    const emailsToRemove = duplicateRecipients
      .filter(d => !selectedEmails.has(d.email))
      .map(d => d.email);
    
    const remainingEmails = allOriginalEmails.filter(email => !emailsToRemove.includes(email));
    
    if (remainingEmails.length > 0) {
      onProceedWithSelected(remainingEmails);
    } else {
      onCancel();
    }
  };

  const formatLastSent = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const timeAgo = formatDistanceToNow(date, { addSuffix: true });
      const formattedDate = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
      return `${timeAgo} (${formattedDate})`;
    } catch {
      return dateString;
    }
  };

  const getDaysAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" style={{ zIndex: 60 }}>
        <DialogHeader>
          <DialogTitle className="flex items-center text-lg">
            <i className="fas fa-exclamation-triangle text-amber-500 mr-2"></i>
            Duplicate Email Warning
          </DialogTitle>
          <DialogDescription>
            Some recipients have already received emails from you. Sending again might be seen as spam or unprofessional.
          </DialogDescription>
        </DialogHeader>

        <Alert className="border-orange-200 bg-orange-50">
          <AlertDescription className="text-sm">
            <strong>{duplicateRecipients.length}</strong> out of your selected recipients have already received emails from you.
            Review the list below and decide how to proceed.
          </AlertDescription>
        </Alert>

        <div className="flex-1 overflow-y-auto my-4 space-y-3 max-h-[400px]">
          {duplicateRecipients.map((recipient) => {
            const daysAgo = getDaysAgo(recipient.last_sent_date);
            const isRecent = daysAgo !== null && daysAgo < 7;
            
            return (
              <div
                key={recipient.email}
                className={`border rounded-lg p-4 ${
                  isRecent ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <Checkbox
                    checked={selectedEmails.has(recipient.email)}
                    onCheckedChange={() => handleEmailToggle(recipient.email)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">
                          {recipient.player_name || 'Unknown Player'}
                        </p>
                        <p className="text-sm text-gray-500">{recipient.email}</p>
                      </div>
                      {isRecent && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                          Recent
                        </span>
                      )}
                    </div>
                    
                    <div className="text-sm space-y-1">
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-clock mr-2 text-xs"></i>
                        Last sent: {formatLastSent(recipient.last_sent_date)}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-envelope mr-2 text-xs"></i>
                        Subject: "{recipient.last_subject}"
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-file-alt mr-2 text-xs"></i>
                        Template: {recipient.last_template_name || 'Manual (No template)'}
                      </div>
                      <div className="flex items-center text-gray-500">
                        <i className="fas fa-paper-plane mr-2 text-xs"></i>
                        Total emails sent: {recipient.total_emails_sent}
                      </div>
                    </div>

                    {isRecent && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        Sent less than a week ago - high risk of appearing as spam
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t pt-4 space-y-3">
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">Your options:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Uncheck recipients you don't want to email again</li>
              <li>Click "Remove Unchecked & Send" to send only to checked recipients</li>
              <li>Click "Send Anyway" to proceed with all recipients (not recommended)</li>
              <li>Click "Cancel" to go back and revise your email</li>
            </ul>
          </div>

          <div className="flex justify-between gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleRemoveSelected}
                disabled={selectedEmails.size === 0}
              >
                Remove Unchecked & Send ({selectedEmails.size})
              </Button>
              
              <Button
                variant="destructive"
                onClick={() => onProceedWithSelected(duplicateRecipients.map(r => r.email))}
              >
                Send Anyway (All)
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}